import React, {useState} from 'react'
import {css} from '@emotion/react'
import styled from '@emotion/styled'
import {MeasurementSystemType} from 'types/Localization'
import {imageProvider as partnersImageProvider} from 'utils/imageProvider'
import {WidgetLabelTypes} from 'utils/label'
import {DatePicker} from 'widgets/components/DatePicker'
import {getNumberOfNights} from 'widgets/helpers/offer'

import {useDeviceLayout} from '@daedalus/atlas/context/deviceLayout'
import {Divider} from '@daedalus/atlas/Divider'
import {BackendError} from '@findhotel/shared/src/booking/BackendError'
import {SkeletonLoader} from '@daedalus/atlas/SkeletonLoader'
import {brands} from '@daedalus/core/src/brand/config'
import {DatePickerType} from '@daedalus/core/src/datePicker/types'
import {TaxDisplayLogic} from '@daedalus/core/src/offer/types/TaxDisplayLogic'
import {
  addIdsToRoomsSplit,
  numberOfGuests,
  roomsToConfigurationObject
} from '@daedalus/core/src/room/business/roomConfiguration'
import {UniqueRoomSplitType} from '@daedalus/core/src/room/types/RoomConfiguration'
import {ImageProvider} from '@daedalus/core/src/staticContent/types/ImageProvider'

import {Offer} from '../../../types/partnersApi/types'
import {SoldOut} from '../../SoldOut'
import {WidgetContainer} from '../../WidgetContainer'
import {GuestPickerPopover} from '../GuestPickerPopover'
import {HotelInfo, HotelInfoSkeleton} from '../HotelInfo'
import {ComposedOffers, ComposedOffersSkeleton} from '../Offers/OffersList'
import {WidgetHeader} from '../WidgetHeader'

const SoldOutWrapper = styled('div')(
  ({theme}) => css`
    display: flex;
    min-height: 271px;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    padding: 0 ${theme.layout.spacing.s400}px;
  `
)

const ErrorWrapper = styled('div')(
  ({theme}) => css`
    display: flex;
    min-height: 560px;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    padding: 0 ${theme.layout.spacing.s400}px;
  `
)

export interface HotelCardProps {
  checkIn?: string
  checkOut?: string
  rooms: string
  distanceUnit: MeasurementSystemType
  hotelName?: string
  starRating?: number
  address?: string
  distanceFromTarget?: number
  hotelImages?: string[]
  cityName?: string
  hasError?: boolean
  offers?: Offer[]
  isFetching?: boolean
  taxDisplayLogic: TaxDisplayLogic
  partnerKey: string
  label?: string
  imageProvider?: ImageProvider
  onDatesUpdate: (checkIn: string, checkOut: string) => void
  onGuestsUpdate: (rooms: UniqueRoomSplitType[]) => void
  onDatesSubmit: () => void
  refetch: () => void
}

const HotelCardSkeleton = ({
  withHeaderSkeleton
}: {
  withHeaderSkeleton?: boolean
}) => (
  <>
    {withHeaderSkeleton && (
      <SkeletonLoader height={42} marginTop={12} marginBottom={12} />
    )}
    <HotelInfoSkeleton />
    <ComposedOffersSkeleton />
  </>
)

export const HotelCard = ({
  checkIn,
  checkOut,
  rooms,
  hotelName,
  starRating,
  address,
  distanceFromTarget,
  distanceUnit,
  hotelImages,
  cityName,
  hasError = false,
  offers,
  isFetching,
  taxDisplayLogic,
  partnerKey,
  label,
  imageProvider = partnersImageProvider,
  onDatesUpdate,
  onGuestsUpdate,
  onDatesSubmit,
  refetch
}: HotelCardProps) => {
  const {isMobile} = useDeviceLayout()
  const isSoldOut = offers && offers.length === 0

  const {roomsSplit, numberOfRooms} = roomsToConfigurationObject(rooms)
  const guests = addIdsToRoomsSplit(roomsSplit)
  const totalGuests = numberOfGuests(rooms)

  const onSupportButtonClick = () => {
    window.open(brands.vio.contactUrl, '_blank')
  }

  const [openedDatePickerType, setOpenedDatePickerType] =
    useState<DatePickerType | null>(null)

  return (
    <WidgetContainer
      borderType="solid"
      height={565}
      partnerKey={partnerKey}
      label={label}
      widgetType={WidgetLabelTypes.hotelCard}
    >
      {hasError && (
        <ErrorWrapper>
          <BackendError
            onReloadClick={() => refetch()}
            onSupportLinkClick={onSupportButtonClick}
          />
        </ErrorWrapper>
      )}

      {!hasError && checkIn && checkOut && (
        <WidgetHeader
          DatePicker={
            <DatePicker
              isOverlay={isMobile}
              checkIn={checkIn}
              checkOut={checkOut}
              openedDatePickerType={openedDatePickerType}
              onSetOpenedDatePickerType={setOpenedDatePickerType}
              onUpdate={onDatesUpdate}
              onSubmit={onDatesSubmit}
            />
          }
          GuestPicker={
            <GuestPickerPopover
              guests={guests}
              totalValueOfGuests={totalGuests}
              onSubmit={onGuestsUpdate}
            />
          }
        />
      )}

      {isFetching && (
        // Show header skeleton only if dates are not available yet (initial loading)
        <HotelCardSkeleton withHeaderSkeleton={!(checkIn && checkOut)} />
      )}

      {!isFetching && !hasError && checkIn && checkOut && (
        <>
          <HotelInfo
            hotelName={hotelName}
            starRating={starRating}
            distanceFromTarget={distanceFromTarget}
            distanceUnit={distanceUnit}
            address={address}
            hotelImages={hotelImages}
            cityName={cityName}
            imageProvider={imageProvider}
          />
          {isSoldOut ? (
            <>
              <Divider />
              <SoldOutWrapper>
                <SoldOut
                  checkInDate={checkIn}
                  checkOutDate={checkOut}
                  onClick={() =>
                    setOpenedDatePickerType(DatePickerType.CheckIn)
                  }
                />
              </SoldOutWrapper>
            </>
          ) : (
            <ComposedOffers
              offers={offers}
              numberOfRooms={numberOfRooms}
              numberOfNights={getNumberOfNights(checkIn, checkOut)}
              taxDisplayLogic={taxDisplayLogic}
              partnerKey={partnerKey}
              label={label}
            />
          )}
        </>
      )}
    </WidgetContainer>
  )
}
